import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import TeamSearch from './TeamSearch';
import ConversationSelector from './ConversationSelector';
import ConversationSkeleton from '../chat/ConversationSkeleton';
import {
	getPreviousTeamConversations,
	setActiveTeamConversation,
} from '../../../actions/teams';
import getEarliestTeamConversation from '../../utils/getEarliestTeamConversation';
import { AtSign, Loader2, SquarePen, Star } from 'lucide-react';
import { IconButton } from '@/components/ui/custom/IconButton';
import TeamSearchResults from './TeamSearchResults';
import { cn } from '@/lib/utils';
import { MobileHeader, MobileHeaderButton, SegmentedControl } from '@/components/mobile';
import { useMentionCount } from '@/components/mentions/useMentionCount';
import { getShowNewLayout } from '@/components/sidebar/layout-preference';
import { isMobileViewport } from '@/hooks/use-mobile';

// The phone and desktop layouts swap when the window crosses 768px. The
// search box and its results survive that swap, but not leaving Team chat.
const searchMemory = { text: '', results: [] };

function sortConversations(conversations, currentUserId) {
	return conversations.sort((a, b) => {
		const aMuted = a._mutedUsers.includes(currentUserId);
		const bMuted = b._mutedUsers.includes(currentUserId);
		const aUnread = a._unreadUsers.includes(currentUserId);
		const bUnread = b._unreadUsers.includes(currentUserId);

		// If both conversations have the user in _unreadUsers, sort by date
		if (aUnread && bUnread) {
			return (
				new Date(b.latestMessageDate) - new Date(a.latestMessageDate)
			);
		}

		// If both conversations have the user in _mutedUsers but not in _unreadUsers, sort by muted status
		if (aMuted && !aUnread && bMuted && !bUnread) {
			return 0; // Both are muted, keep their relative order
		}

		// If only one of the conversations has the user in _unreadUsers, sort by _unreadUsers status
		if (aUnread) return -1;
		if (bUnread) return 1;

		// If only one of the conversations has the user in _mutedUsers, sort by _mutedUsers status
		if (aMuted) return 1;
		if (bMuted) return -1;

		// Otherwise, sort by date
		return new Date(b.latestMessageDate) - new Date(a.latestMessageDate);
	});
}

const TeamConversations = ({
	setCustomerConversationAssignedType,
	handleShowStartNewChatDialog,
	// Phones: a full-screen list with a large title, Groups | Direct messages
	// and the mentions and compose buttons in the header.
	isMobile = false,
}) => {
	const users = useSelector((state) => state.users);
	const auth = useSelector((state) => state.auth);
	const teams = useSelector((state) => state.teams);
	const { teamConversations, _activeTeamConversation } = teams;
	const dispatch = useDispatch();
	const [loadingMoreConversations, setLoadingMoreConversations] =
		useState(false);
	const [isSearching, setIsSearching] = useState(false);
	const [searchResults, setSearchResults] = useState(searchMemory.results);
	const [searchText, setSearchText] = useState(searchMemory.text);
	const webchat = useSelector((state) => state.webchat);
	const navigate = useNavigate();
	const teamMentionCount = useMentionCount(true);

	useEffect(() => {
		searchMemory.text = searchText;
		searchMemory.results = searchResults;
	}, [searchText, searchResults]);

	const isMobileRef = useRef(isMobile);
	isMobileRef.current = isMobile;
	useEffect(
		() => () => {
			// Unmounted without the window crossing 768px: the person left.
			if (isMobileViewport() === isMobileRef.current) {
				searchMemory.text = '';
				searchMemory.results = [];
			}
		},
		[]
	);
	const directMessage = webchat.assignedTypeSelected === 'DIRECT_MESSAGES';
	const filteredDirectMessages = teamConversations?.filter(
		(tc) => tc.isDirectMessage
	);

	// No auth: the sign-up preview keeps this list's old height.
	const showNewLayout = getShowNewLayout();

	const filteredGroups = teamConversations?.filter(
		(tc) => !tc.isDirectMessage && !tc.isRunner
	);

	const conversations = directMessage
		? filteredDirectMessages
		: filteredGroups;

	// sort conversation by the date of the latest message being sent
	const getMostRecentConversationId = () => {
		if (conversations?.length > 0) {
			const sortedConversations = [...conversations].sort((a, b) =>
				new Date(a.latestMessageDate) > new Date(b.latestMessageDate)
					? -1
					: 1
			);
			return sortedConversations[0]._id;
		}
		return null;
	};

	const loadMoreTeamConversations = async () => {
		const isDirectMessage = directMessage;

		const earliestConversation = getEarliestTeamConversation({
			isDirectMessage,
			teamConversations,
		});

		const earliestConversationDate = earliestConversation
			? new Date(earliestConversation.latestMessageDate)
			: new Date();

		setLoadingMoreConversations(true);

		await dispatch(
			getPreviousTeamConversations({
				loadConversationsBefore: earliestConversationDate,
				isDirectMessage,
			})
		);

		setLoadingMoreConversations(false);
	};

	const starredConversations = conversations?.filter((conversation) =>
		conversation._starredUsers?.includes(auth.user._id)
	);
	const regularConversations = conversations?.filter(
		(conversation) => !conversation._starredUsers?.includes(auth.user._id)
	);

	const hasStarredConversations = starredConversations?.length > 0;

	const renderConversations = (conversationsToRender) => {
		return sortConversations(conversationsToRender, auth.user._id).map(
			(conversation) => {
				const latestMessage =
					conversation.teamMessages &&
					conversation.teamMessages.length > 0
						? conversation.teamMessages.reduce((prev, current) =>
								new Date(current.createdAt) >
								new Date(prev.createdAt)
									? current
									: prev
						  )
						: null;

				const user = users.find((u) => u._id === latestMessage?._user);
				const userFilteredFromConversation = conversation._users.find(
					(u) => u !== auth.user._id
				);
				const userFromDirectMessage = directMessage
					? users.find((u) => u._id === userFilteredFromConversation)
					: null;

				const name = directMessage
					? userFromDirectMessage?.name
					: user
					? user.name
					: 'Unknown User';

				return (
					<ConversationSelector
						assignedTypeSelected={webchat.assignedTypeSelected}
						conversation={conversation}
						latestMessage={latestMessage}
						name={name}
						_activeTeamConversation={_activeTeamConversation}
						_activeUser={auth.user._id}
						key={conversation._id}
						userFromDirectMessage={userFromDirectMessage}
					/>
				);
			}
		);
	};

	// sets the most recent conversation to active when the teamConversations are retrieved.
	// Phones show the list on its own and open a chat from its URL, so
	// nothing opens (or gets marked read) until it is tapped.
	useEffect(() => {
		if (
			!isMobile &&
			(webchat.assignedTypeSelected === 'GROUPS' ||
				webchat.assignedTypeSelected === 'DIRECT_MESSAGES') &&
			_activeTeamConversation === null
		) {
			dispatch(setActiveTeamConversation(getMostRecentConversationId()));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		webchat.assignedTypeSelected,
		teamConversations,
		_activeTeamConversation,
		isMobile,
	]);

	const teamSearch = (
		<TeamSearch
			assignedTypeSelected={webchat.assignedTypeSelected}
			setCustomerConversationAssignedType={
				setCustomerConversationAssignedType
			}
			setIsSearching={setIsSearching}
			setSearchResults={setSearchResults}
			isSearching={isSearching}
			searchText={searchText}
			setSearchText={setSearchText}
		/>
	);

	const hasUnread = (list) =>
		(list || []).some((tc) => tc._unreadUsers?.includes(auth.user._id));
	const unreadDot = (show) =>
		show ? (
			<>
				<span className="size-2 rounded-full bg-blue-500" aria-hidden="true" />
				<span className="sr-only">, unread</span>
			</>
		) : null;

	const mobileHeader = isMobile && (
		<MobileHeader
			largeTitle
			title="Team"
			right={
				<>
					<MobileHeaderButton
						aria-label={
							teamMentionCount > 0
								? `Mentions, ${teamMentionCount} unread`
								: 'Mentions'
						}
						onClick={() => navigate('/team-mentions')}
						className="relative"
					>
						<AtSign />
						{teamMentionCount > 0 && (
							<span
								aria-hidden="true"
								className="absolute right-1 top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-semibold leading-none text-white"
							>
								{teamMentionCount > 99 ? '99+' : teamMentionCount}
							</span>
						)}
					</MobileHeaderButton>
					<MobileHeaderButton
						aria-label="New chat"
						data-onboarding="teams-new-chat"
						onClick={() => handleShowStartNewChatDialog(true)}
					>
						<SquarePen />
					</MobileHeaderButton>
				</>
			}
		>
			<div className="space-y-2.5 px-4 pb-3">
				<SegmentedControl
					aria-label="Show"
					value={directMessage ? 'dms' : 'groups'}
					onValueChange={(value) =>
						navigate(value === 'dms' ? '/groupchat/dms' : '/groupchat/groups', {
							replace: true,
						})
					}
					options={[
						{
							value: 'groups',
							label: 'Groups',
							badge: unreadDot(hasUnread(filteredGroups)),
						},
						{
							value: 'dms',
							label: 'Direct messages',
							badge: unreadDot(hasUnread(filteredDirectMessages)),
						},
					]}
				/>
				{teamSearch}
			</div>
		</MobileHeader>
	);

	return (
		<div
			className={cn(
				'm-0 flex max-h-[97dvh] flex-col',
				// Medium windows (landscape phones, iPads) are too short for the
				// dvh heights below, so the list fills the panel and scrolls.
				'md:max-xl:h-full md:max-xl:max-h-full',
				isMobile && 'h-full max-h-none min-h-0 bg-background'
			)}
			data-onboarding="teams-conversation-list"
		>
			{mobileHeader}
			{!isMobile && (
			<div className="flex w-full items-center gap-2 border-b border-border/60 px-3 pb-2.5 pt-3">
				<div className="min-w-0 flex-1">{teamSearch}</div>
				<IconButton
					variant="ghost"
					onClick={() => {
						handleShowStartNewChatDialog(true);
					}}
					toolTipSide="bottom"
					toolTipTitle="New chat"
					aria-label="New chat"
					data-onboarding="teams-new-chat"
					className="size-9 shrink-0 rounded-lg border border-border/60 bg-background shadow-none hover:bg-stone-100 dark:hover:bg-stone-800"
				>
					<SquarePen className="size-4" />
				</IconButton>
			</div>
			)}
			<div
				className={cn(
					showNewLayout ? 'md:h-[85dvh]' : 'md:h-[80dvh]',
					'2xl:h-[90dvh] overflow-y-auto overflow-x-hidden',
					'md:max-xl:h-auto md:max-xl:min-h-0 md:max-xl:flex-1',
					isMobile && 'min-h-0 flex-1 overscroll-contain'
				)}
				style={{
					scrollbarWidth: 'none',
				}}
			>
				<div>
					{!isSearching && searchResults.length === 0 ? (
						<div className="flex flex-col">
							{hasStarredConversations && (
								<div className="flex flex-col">
									<div className="flex items-center gap-1.5 px-4 pb-1 pt-3 text-xs font-medium text-muted-foreground">
										<Star className="size-3.5" />
										Starred
									</div>
									{renderConversations(starredConversations)}
									<div className="px-4 pb-1 pt-3 text-xs font-medium text-muted-foreground">
										{directMessage ? 'Direct messages' : 'Groups'}
									</div>
								</div>
							)}
							{teamConversations ? (
								renderConversations(regularConversations)
							) : (
								<ConversationSkeleton />
							)}
						</div>
					) : (
						<TeamSearchResults
							searchResults={searchResults}
							setIsSearching={setIsSearching}
							setCustomerConversationAssignedType={
								setCustomerConversationAssignedType
							}
							searchText={searchText}
							setSearchResults={setSearchResults}
						/>
					)}
				</div>
				{teamConversations?.length > 10 && (
					<button
						type="button"
						className={cn(
							'flex w-full items-center justify-center gap-2 py-3 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-60',
							isMobile && 'min-h-12 text-sm'
						)}
						disabled={loadingMoreConversations}
						onClick={loadMoreTeamConversations}
					>
						{loadingMoreConversations && (
							<Loader2 className="size-3.5 animate-spin" />
						)}
						{directMessage ? 'Load more direct messages' : 'Load more groups'}
					</button>
				)}
			</div>
		</div>
	);
};

export default TeamConversations;
